import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, User, Lock, Trash2, Download } from "lucide-react";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  const [exporting, setExporting] = useState(false);

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/auth/export");
      const stamp = new Date().toISOString().slice(0, 10);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `planiq-mine-data-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Dataeksporten er lastet ned.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kunne ikke laste ned dataene");
    } finally {
      setExporting(false);
    }
  };

  const saveName = async (e) => {
    e.preventDefault();
    setSavingName(true);
    try {
      const { data } = await api.patch("/auth/profile", { name });
      setUser(data);
      toast.success("Navnet er oppdatert");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kunne ikke lagre");
    } finally {
      setSavingName(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("Nytt passord må være minst 6 tegn");
      return;
    }
    setSavingPw(true);
    try {
      await api.patch("/auth/profile", { current_password: currentPassword, new_password: newPassword });
      setCurrentPassword("");
      setNewPassword("");
      toast.success("Passordet er endret");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kunne ikke endre passord");
    } finally {
      setSavingPw(false);
    }
  };

  const isGoogle = user?.auth_provider === "google";

  const canConfirmDelete = isGoogle
    ? deleteConfirm.trim().toUpperCase() === "SLETT"
    : deletePassword.length > 0;

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      const payload = isGoogle
        ? { confirm: deleteConfirm.trim().toUpperCase() }
        : { password: deletePassword };
      await api.delete("/auth/account", { data: payload });
      toast.success("Kontoen din er slettet.");
      setUser(null);
      // Full reload for å tømme all klient-state
      window.location.href = "/";
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kunne ikke slette kontoen");
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="max-w-2xl mx-auto px-6 lg:px-8 py-12">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8" data-testid="profile-back">
          <ArrowLeft className="h-4 w-4" /> Til dashbord
        </Link>

        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Profil</p>
        <h1 className="mt-2 text-4xl font-medium tracking-tight text-slate-900" data-testid="profile-title">Din konto</h1>
        <p className="mt-2 text-slate-600">Oppdater navn og endre passord for kontoen din.</p>

        <Card className="mt-10 bg-white border border-gray-200 rounded-2xl">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <User className="h-5 w-5 text-slate-700" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Personinfo</p>
                <p className="text-xs text-slate-500">E-post: {user?.email}</p>
              </div>
            </div>
            <form onSubmit={saveName} className="space-y-4">
              <div>
                <Label htmlFor="name">Navn</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1.5 h-11 rounded-xl"
                  data-testid="profile-name-input"
                />
              </div>
              <Button
                type="submit"
                disabled={savingName || !name.trim()}
                className="rounded-full bg-slate-900 hover:bg-slate-800"
                data-testid="profile-save-name-btn"
              >
                {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : "Lagre navn"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="mt-6 bg-white border border-gray-200 rounded-2xl">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <Lock className="h-5 w-5 text-slate-700" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Passord</p>
                <p className="text-xs text-slate-500">
                  {isGoogle ? "Du logger inn med Google - ingen passordendring nødvendig." : "Endre passordet ditt."}
                </p>
              </div>
            </div>
            {!isGoogle && (
              <form onSubmit={changePassword} className="space-y-4">
                <div>
                  <Label htmlFor="cur">Nåværende passord</Label>
                  <Input
                    id="cur"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="mt-1.5 h-11 rounded-xl"
                    data-testid="profile-current-pw"
                  />
                </div>
                <div>
                  <Label htmlFor="new">Nytt passord</Label>
                  <Input
                    id="new"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="mt-1.5 h-11 rounded-xl"
                    data-testid="profile-new-pw"
                  />
                  <p className="text-xs text-slate-400 mt-1.5">Minst 6 tegn.</p>
                </div>
                <Button
                  type="submit"
                  disabled={savingPw || !currentPassword || !newPassword}
                  className="rounded-full bg-slate-900 hover:bg-slate-800"
                  data-testid="profile-save-pw-btn"
                >
                  {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : "Endre passord"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Dataeksport – GDPR art. 15/20 */}
        <Card className="mt-6 bg-white border border-gray-200 rounded-2xl" data-testid="data-export-card">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <Download className="h-5 w-5 text-slate-700" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Last ned mine data</p>
                <p className="text-xs text-slate-500">GDPR-rett til innsyn og dataportabilitet</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mb-5">
              Få en JSON-fil med kontoinformasjonen din, alle forretningsplanene dine og
              betalingskvitteringer. Systemdata og andre brukeres opplysninger er ikke inkludert.
            </p>
            <Button
              onClick={exportData}
              disabled={exporting}
              variant="outline"
              className="rounded-full border-slate-300 hover:bg-slate-50"
              data-testid="data-export-btn"
            >
              {exporting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Genererer …</>
              ) : (
                <><Download className="h-4 w-4 mr-2" /> Last ned JSON</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Farlig sone – bruker-styrt sletting */}
        <Card className="mt-6 bg-white border border-red-200 rounded-2xl" data-testid="danger-zone-card">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-xl bg-red-50 flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Slett kontoen din</p>
                <p className="text-xs text-slate-500">Handlingen kan ikke angres.</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mb-5">
              Alle forretningsplaner, utkast og innloggingsøkter slettes permanent.
              Kontoen anonymiseres (e-post og navn fjernes). Betalingskvitteringer beholdes i
              anonymisert form i 5 år, slik norsk bokføringslov krever.
            </p>

            <AlertDialog open={deleteOpen} onOpenChange={(o) => { setDeleteOpen(o); if (!o) { setDeletePassword(""); setDeleteConfirm(""); } }}>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                  data-testid="delete-account-open-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Slett kontoen min
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="delete-account-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Er du helt sikker?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Denne handlingen sletter kontoen din, alle planer og aktive innlogginger permanent.
                    Betalingskvitteringer beholdes i anonymisert form i 5 år (bokføringsloven).
                  </AlertDialogDescription>
                </AlertDialogHeader>

                <div className="pt-2">
                  {isGoogle ? (
                    <div>
                      <Label htmlFor="delete-confirm" className="text-sm text-slate-700">
                        Skriv <span className="font-mono font-semibold">SLETT</span> for å bekrefte
                      </Label>
                      <Input
                        id="delete-confirm"
                        value={deleteConfirm}
                        onChange={(e) => setDeleteConfirm(e.target.value)}
                        placeholder="SLETT"
                        className="mt-1.5 h-11 rounded-xl"
                        data-testid="delete-account-confirm-input"
                        autoComplete="off"
                      />
                    </div>
                  ) : (
                    <div>
                      <Label htmlFor="delete-pw" className="text-sm text-slate-700">Bekreft med passordet ditt</Label>
                      <Input
                        id="delete-pw"
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        className="mt-1.5 h-11 rounded-xl"
                        data-testid="delete-account-password-input"
                        autoComplete="current-password"
                      />
                    </div>
                  )}
                </div>

                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="delete-account-cancel-btn" disabled={deleting}>Avbryt</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => { e.preventDefault(); deleteAccount(); }}
                    disabled={!canConfirmDelete || deleting}
                    className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                    data-testid="delete-account-confirm-btn"
                  >
                    {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Slett kontoen permanent"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
